import { useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAssignable } from "@/hooks/useMeta";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";

const INTERNAL = ["sales_head", "region_head", "sales_manager", "sales_coordinator", "salesperson", "bd", "sc"];
const MANAGERS = ["super_admin", "sales_head", "region_head", "sales_manager"];

export function AssignOwnerSelect({ entityType, entityId, ownerId, onAssigned, compact = false }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const { data: users = [] } = useAssignable();

  const canAssign = MANAGERS.includes(user?.role) || ownerId === user?.id;
  if (!canAssign || user?.role === "dealer" || user?.role === "factory" || user?.role === "management") return null;

  const options = users
    .filter((u) => INTERNAL.includes(u.role) && u.id !== ownerId)
    .sort((a, b) => (a.region || "").localeCompare(b.region || "") || a.name.localeCompare(b.name));

  const assign = async (uid) => {
    setBusy(true);
    try {
      const path = entityType === "lead" ? `/leads/${entityId}/assign` : `/opportunities/${entityId}/assign`;
      const { data } = await api.post(path, { user_id: uid });
      toast.success(`Assigned to ${data.owner_name}`);
      onAssigned?.(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Select value="" onValueChange={assign} disabled={busy}>
      <SelectTrigger
        onClick={(e) => e.stopPropagation()}
        className={compact ? "h-8 w-[120px] text-xs" : "h-9 w-[150px]"}
        data-testid={`assign-owner-${entityId}`}>
        <span className="flex items-center gap-1.5 text-slate-600">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Assign to…
        </span>
      </SelectTrigger>
      <SelectContent className="max-h-64" onClick={(e) => e.stopPropagation()}>
        {options.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            {u.name}{u.region ? ` · ${u.region}` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
